import { PaymentConfig, PaymentTransaction, Registration, EventOrganizer } from '../lib/supabase';
import { supabase } from '../lib/supabase';
import {
  APP_BUILD_ID,
  buildPayFastCheckout,
  getPayFastCallbackUrls,
  getPayFastSiteOrigin,
  isMerchantSelfPayment,
  MERCHANT_SELF_PAYMENT_MESSAGE,
} from '../utils/payfastCheckout';
import { verifyPayFastSignature } from '../utils/payfastSignature';

export interface PaymentRequest {
  registrationId: string;
  eventId: string;
  organizerId: string;
  amount: number;
  currency: string;
  userEmail: string;
  userName: string;
  userId: string;
  eventTitle?: string;
}

export interface PaymentResponse {
  success: boolean;
  paymentUrl?: string;
  paymentReference?: string;
  instructions?: string;
  error?: string;
  payfastCheckout?: {
    action: string;
    fields: Record<string, string>;
    passphrase?: string;
    buildId?: string;
  };
}

// Simple hash function for other uses (non-PayFast)
function simpleHash(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash).toString(16);
}

export class PaymentService {
  // PayFast configuration from environment variables
  private static readonly PAYFAST_ENVIRONMENT = process.env.REACT_APP_PAYFAST_ENVIRONMENT || 'production';
  
  // Validate that required environment variables are set
  private static validateEnvironmentVariables() {
    const requiredVars = [
      'REACT_APP_ADMIN_PAYFAST_MERCHANT_ID',
      'REACT_APP_ADMIN_PAYFAST_MERCHANT_KEY',
      'REACT_APP_ADMIN_PAYFAST_PASSPHRASE'
    ];
    
    const missing = requiredVars.filter(varName => !process.env[varName]);
    
    if (missing.length > 0) {
      console.error(`❌ Missing required PayFast environment variables: ${missing.join(', ')}`);
      return false;
    }
    
    // Add detailed passphrase debugging
    const passphrase = process.env.REACT_APP_ADMIN_PAYFAST_PASSPHRASE;
    console.log('🔍 PayFast Passphrase Debug:', {
      exists: !!passphrase,
      length: passphrase?.length,
      trimmed_length: passphrase?.trim().length,
      first_5_chars: passphrase ? `${passphrase.substring(0, 5)}...` : 'N/A',
      has_spaces: passphrase?.includes(' '),
      is_empty: !passphrase || passphrase.trim().length === 0,
      raw_value: passphrase ? 'Present' : 'Missing'
    });
    
    console.log('PayFast environment variables are set:', {
      REACT_APP_PAYFAST_ENVIRONMENT: process.env.REACT_APP_PAYFAST_ENVIRONMENT || 'Not set',
      REACT_APP_ADMIN_PAYFAST_MERCHANT_ID: process.env.REACT_APP_ADMIN_PAYFAST_MERCHANT_ID || 'Not set',
      REACT_APP_ADMIN_PAYFAST_MERCHANT_KEY: process.env.REACT_APP_ADMIN_PAYFAST_MERCHANT_KEY || 'Not set',
      REACT_APP_ADMIN_PAYFAST_PASSPHRASE: process.env.REACT_APP_ADMIN_PAYFAST_PASSPHRASE ? 'Set' : 'Not set'
    });
    
    return true;
  }
  
  private static readonly ADMIN_PAYFAST_MERCHANT_ID = process.env.REACT_APP_ADMIN_PAYFAST_MERCHANT_ID;
  private static readonly ADMIN_PAYFAST_MERCHANT_KEY = process.env.REACT_APP_ADMIN_PAYFAST_MERCHANT_KEY;
  private static readonly ADMIN_PAYFAST_PASSPHRASE = process.env.REACT_APP_ADMIN_PAYFAST_PASSPHRASE;
  
  private static usesAdminPayFastMerchant(merchantId?: string | null): boolean {
    return !!(
      PaymentService.ADMIN_PAYFAST_MERCHANT_ID &&
      merchantId &&
      merchantId.trim() === PaymentService.ADMIN_PAYFAST_MERCHANT_ID.trim()
    );
  }
  
  // Get the appropriate PayFast URL based on environment
  private static get PAYFAST_URL(): string {
    const environment = PaymentService.PAYFAST_ENVIRONMENT;
    console.log('Getting PayFast URL for environment:', environment);
    
    // Use environment variables for URLs if available, otherwise use standard PayFast URLs
    const productionUrl = process.env.REACT_APP_PAYFAST_PRODUCTION_URL || 'https://www.payfast.co.za/eng/process';
    const sandboxUrl = process.env.REACT_APP_PAYFAST_SANDBOX_URL || 'https://sandbox.payfast.co.za/eng/process';
    
    const url = environment === 'production' ? productionUrl : sandboxUrl;
    
    console.log('Using PayFast URL:', url);
    return url;
  }

  static async getOrganizerConfig(organizerId: string): Promise<EventOrganizer | null> {
    // Validate environment variables first
    this.validateEnvironmentVariables();
    console.log('🔍 PaymentService.getOrganizerConfig called with organizerId:', organizerId);
    try {
      // IMPORTANT: For any event, we need to first check if it was created by an admin
      // This is the critical fix - we need to use admin PayFast config for ANY admin-created event
      
      // First, always return admin PayFast config for admin events
      // This is our default fallback to ensure admin events always work
      const adminPayFastConfig: EventOrganizer = {
        id: organizerId,
        name: 'GOMTB Admin',
        email: 'admin@gomtbregister.co.za',
        phone: '+27000000000',
        organization: 'GOMTB Register',
        payment_gateway: 'payfast',
        payment_config: {
          merchant_id: PaymentService.ADMIN_PAYFAST_MERCHANT_ID,
          merchant_key: PaymentService.ADMIN_PAYFAST_MERCHANT_KEY,
          passphrase: PaymentService.ADMIN_PAYFAST_PASSPHRASE,
          sandbox: PaymentService.PAYFAST_ENVIRONMENT === 'sandbox',
          is_admin_config: true
        },
        is_verified: true,
        created_at: new Date().toISOString()
      };
      
      console.log('Admin PayFast config being used:', {
        merchantId: PaymentService.ADMIN_PAYFAST_MERCHANT_ID ? 'Set from env' : 'Not set',
        merchantKey: PaymentService.ADMIN_PAYFAST_MERCHANT_KEY ? 'Set from env' : 'Not set',
        passphrase: PaymentService.ADMIN_PAYFAST_PASSPHRASE ? 'Set from env' : 'Not set',
        environment: PaymentService.PAYFAST_ENVIRONMENT || 'Not set'
      });
      
      // Debug actual values (be careful not to log sensitive data in production)
      console.log('Environment variables debug:', {
        REACT_APP_ADMIN_PAYFAST_MERCHANT_ID: process.env.REACT_APP_ADMIN_PAYFAST_MERCHANT_ID ? 'Set' : 'Not set',
        REACT_APP_ADMIN_PAYFAST_MERCHANT_KEY: process.env.REACT_APP_ADMIN_PAYFAST_MERCHANT_KEY ? 'Set' : 'Not set',
        REACT_APP_ADMIN_PAYFAST_PASSPHRASE: process.env.REACT_APP_ADMIN_PAYFAST_PASSPHRASE ? 'Set' : 'Not set',
        REACT_APP_PAYFAST_ENVIRONMENT: process.env.REACT_APP_PAYFAST_ENVIRONMENT || 'Not set'
      });
      
      // Look up organizer user
      const { data: user, error: userError } = await supabase
        .from('users')
        .select('email, name')
        .eq('id', organizerId)
        .single();
      
      console.log('User check result:', { user, userError });
      
      if (userError || !user) {
        console.log('⚠️ Organizer user not found - defaulting to admin PayFast config');
        return adminPayFastConfig;
      }

      adminPayFastConfig.email = user.email;
      adminPayFastConfig.name = user.name || adminPayFastConfig.name;

      // event_organizers may already flag admin PayFast (set when admin creates an event)
      const { data: eventOrganizer } = await supabase
        .from('event_organizers')
        .select('payment_gateway, payment_config')
        .eq('id', organizerId)
        .maybeSingle();

      if (eventOrganizer?.payment_config?.is_admin_config === true) {
        console.log('✅ Using admin PayFast configuration from event_organizers for:', user.email);
        return adminPayFastConfig;
      }

      // Admin role lives in coordinator_profiles (not users.role)
      const { data: profile } = await supabase
        .from('coordinator_profiles')
        .select('role, status')
        .eq('user_email', user.email)
        .eq('status', 'active')
        .maybeSingle();

      if (profile?.role === 'admin') {
        console.log('✅ Using admin PayFast configuration for admin profile:', user.email);
        return adminPayFastConfig;
      }
      
      // Non-admin coordinator: use their PayFast config only if fully configured
      if (user) {
        const { CoordinatorPaymentService } = await import('./coordinatorPaymentService');
        const coordinatorConfig = await CoordinatorPaymentService.getCoordinatorPaymentConfig(user.email);
        
        if (
          coordinatorConfig?.payment_gateway === 'payfast' &&
          coordinatorConfig.payfast_merchant_id &&
          coordinatorConfig.payfast_merchant_key &&
          coordinatorConfig.payfast_passphrase
        ) {
          // Same merchant account as admin → always use env passphrase (matches /payfast-test)
          if (PaymentService.usesAdminPayFastMerchant(coordinatorConfig.payfast_merchant_id)) {
            console.log('✅ Coordinator uses admin merchant ID — using env PayFast credentials for:', user.email);
            return adminPayFastConfig;
          }

          console.log('✅ Using coordinator PayFast configuration for:', user.email);
          return {
            id: organizerId,
            name: coordinatorConfig.coordinator_name || user.name || 'Event Coordinator',
            email: user.email,
            phone: '+27000000000',
            organization: 'GOMTB Register',
            payment_gateway: 'payfast',
            payment_config: {
              merchant_id: coordinatorConfig.payfast_merchant_id,
              merchant_key: coordinatorConfig.payfast_merchant_key,
              passphrase: coordinatorConfig.payfast_passphrase,
              sandbox: coordinatorConfig.payfast_sandbox,
            },
            is_verified: true,
            created_at: new Date().toISOString()
          } as EventOrganizer;
        }

        if (coordinatorConfig?.payment_gateway === 'manual') {
          console.log('✅ Using coordinator manual payment configuration for:', user.email);
          return {
            id: organizerId,
            name: coordinatorConfig.coordinator_name || user.name || 'Event Coordinator',
            email: user.email,
            phone: '+27000000000',
            organization: 'GOMTB Register',
            payment_gateway: 'manual',
            payment_config: {
              banking_details: coordinatorConfig.bank_name ? {
                bank_name: coordinatorConfig.bank_name,
                account_holder: coordinatorConfig.account_holder,
                account_number: coordinatorConfig.account_number,
                branch_code: coordinatorConfig.branch_code,
                account_type: 'Cheque',
                reference_format: 'Use registration reference'
              } : undefined
            },
            is_verified: true,
            created_at: new Date().toISOString()
          } as EventOrganizer;
        }
      }
      
      // Fallback: admin PayFast credentials from env (same as /payfast-test)
      console.log('⚠️ No valid coordinator PayFast config - defaulting to admin PayFast config');
      return adminPayFastConfig;
    } catch (error) {
      console.error('Error fetching organizer config:', error);
      return null;
    }
  }

  static async processPayment(request: PaymentRequest): Promise<PaymentResponse> {
    try {
      const organizerConfig = await this.getOrganizerConfig(request.organizerId);
      
      if (!organizerConfig) {
        return {
          success: false,
          error: 'Payment configuration not found for this organizer'
        };
      }

      // Create payment transaction record with proper UUID format
      // Generate a valid UUID for the transaction ID using a cross-browser compatible method
      let transactionId: string;
      
      try {
        // Try the native crypto.randomUUID() method first
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
          transactionId = crypto.randomUUID();
        } else {
          // Fallback to a UUID v4 generation algorithm
          transactionId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
          });
        }
        console.log('Generated transaction ID:', transactionId);
      } catch (error) {
        console.error('Error generating UUID:', error);
        // Fallback to timestamp-based ID if all else fails
        transactionId = `txn-${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
      }
      
      // Ensure the registration ID is a valid UUID
      console.log('Registration ID received:', request.registrationId);
      
      // Validate that the registration ID is a valid UUID
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      const isValidUuid = uuidRegex.test(request.registrationId);
      
      if (!isValidUuid) {
        console.error('Invalid UUID format for registration_id:', request.registrationId);
        throw new Error('Registration ID must be a valid UUID');
      }
      
      // Generate a payment reference if not already provided
      const paymentReference = transactionId;
      console.log('Using payment reference:', paymentReference);
      
      const { error: transactionError } = await supabase
        .from('payment_transactions')
        .insert({
          id: transactionId,
          registration_id: request.registrationId,
          event_id: request.eventId,
          organizer_id: request.organizerId || null,
          amount: Number(request.amount),
          currency: request.currency,
          payment_method: organizerConfig.payment_gateway,
          payment_reference: paymentReference,
          status: 'pending',
          created_at: new Date().toISOString()
        });

      if (transactionError) {
        console.warn('Could not log payment transaction, continuing to PayFast:', transactionError);
      }

      // Process based on payment gateway
      switch (organizerConfig.payment_gateway) {
        case 'payfast':
          return await this.processPayFastPayment(request, organizerConfig, transactionId);
        
        case 'manual':
          return await this.processManualPayment(request, organizerConfig, transactionId);
        
        default:
          return {
            success: false,
            error: `Unsupported payment gateway: ${organizerConfig.payment_gateway}`
          };
      }
    } catch (error) {
      console.error('Payment processing error:', error);
      const message = error instanceof Error ? error.message : 'Payment processing failed. Please try again.';
      return {
        success: false,
        error: message
      };
    }
  }

  private static async processPayFastPayment(
    request: PaymentRequest, 
    organizerConfig: EventOrganizer, 
    transactionId: string
  ): Promise<PaymentResponse> {
    try {
      console.log('🔎 Processing PayFast payment with organizer config:', organizerConfig);
      const config = organizerConfig.payment_config;
      console.log('🔎 Payment config:', config);
      
      // Determine which PayFast credentials to use
      let merchantId = config?.merchant_id;
      let merchantKey = config?.merchant_key;
      let passphrase = config?.passphrase;

      const useAdminCredentials =
        config?.is_admin_config === true ||
        PaymentService.usesAdminPayFastMerchant(merchantId);

      if (useAdminCredentials) {
        console.log('🔎 Using admin PayFast credentials from environment variables (same as /payfast-test)');
        merchantId = PaymentService.ADMIN_PAYFAST_MERCHANT_ID;
        merchantKey = PaymentService.ADMIN_PAYFAST_MERCHANT_KEY;
        passphrase = PaymentService.ADMIN_PAYFAST_PASSPHRASE;
      } else {
        console.log('🔎 Using coordinator PayFast credentials');
      }
      
      console.log('🔎 PayFast credentials being used:', { 
        merchantId: merchantId ? 'Set' : 'Not set', 
        merchantKey: merchantKey ? 'Set' : 'Not set',
        passphrase: passphrase ? 'Set' : 'Not set',
        useAdminCredentials,
      });
      
      if (!merchantId || !merchantKey) {
        console.error('❌ PayFast configuration incomplete!');
        console.error('Missing credentials:', {
          merchantId: merchantId ? 'Present' : 'Missing',
          merchantKey: merchantKey ? 'Present' : 'Missing',
          passphrase: passphrase ? 'Present' : 'Missing'
        });
        return {
          success: false,
          error: 'PayFast configuration incomplete. Please check your environment variables. Missing: ' + 
                 (!merchantId ? 'MERCHANT_ID ' : '') + 
                 (!merchantKey ? 'MERCHANT_KEY ' : '') +
                 (!passphrase ? 'PASSPHRASE' : '')
        };
      }

      // Match /payfast-test: use current browser origin for return URLs
      const siteOrigin = getPayFastSiteOrigin();
      const callbackUrls = getPayFastCallbackUrls(siteOrigin);
      const nameParts = request.userName.trim().split(/\s+/);
      const useProductionUrl =
        useAdminCredentials || PaymentService.PAYFAST_ENVIRONMENT === 'production';

      const amount = Number(request.amount);
      if (!Number.isFinite(amount) || amount < 5) {
        return {
          success: false,
          error: `Invalid payment amount (R${Number.isFinite(amount) ? amount.toFixed(2) : '0.00'}). PayFast requires at least R5.00.`,
        };
      }

      // PayFast rejects payments from the merchant's own account email
      if (useAdminCredentials && isMerchantSelfPayment(request.userEmail)) {
        return {
          success: false,
          error: MERCHANT_SELF_PAYMENT_MESSAGE,
        };
      }

      const checkout = buildPayFastCheckout({
        merchant_id: merchantId,
        merchant_key: merchantKey,
        passphrase,
        return_url: callbackUrls.return_url,
        cancel_url: callbackUrls.cancel_url,
        notify_url: callbackUrls.notify_url,
        name_first: nameParts[0] || 'User',
        name_last: nameParts.slice(1).join(' ') || nameParts[0] || 'User',
        email_address: request.userEmail.trim(),
        m_payment_id: `reg-${Date.now()}`,
        amount: amount.toFixed(2),
        item_name: request.eventTitle?.trim() || 'Event Registration',
      });

      checkout.action = useProductionUrl
        ? this.PAYFAST_URL
        : config?.sandbox
          ? (process.env.REACT_APP_PAYFAST_SANDBOX_URL || 'https://sandbox.payfast.co.za/eng/process')
          : this.PAYFAST_URL;

      const signatureOk = verifyPayFastSignature(checkout.fields, passphrase);
      console.log('✅ PayFast checkout prepared:', {
        action: checkout.action,
        merchant_id: checkout.fields.merchant_id,
        amount: checkout.fields.amount,
        m_payment_id: checkout.fields.m_payment_id,
        return_url: checkout.fields.return_url,
        notify_url: checkout.fields.notify_url,
        signature: checkout.signature,
        signatureOk,
        useAdminCredentials,
      });
      console.log('PayFast checkout fields JSON:', JSON.stringify(checkout.fields));

      if (!signatureOk) {
        return {
          success: false,
          error: 'PayFast signature verification failed before checkout. Please contact support.',
        };
      }

      return {
        success: true,
        paymentReference: transactionId,
        payfastCheckout: {
          action: checkout.action,
          fields: checkout.fields,
          passphrase,
          buildId: APP_BUILD_ID,
        },
      };
    } catch (error) {
      console.error('PayFast payment error:', error);
      return {
        success: false,
        error: 'PayFast payment setup failed'
      };
    }
  }

  private static async processManualPayment(
    request: PaymentRequest, 
    organizerConfig: EventOrganizer, 
    transactionId: string
  ): Promise<PaymentResponse> {
    const reference = `REF${Date.now()}${simpleHash(request.registrationId).substring(0, 6)}`;
    
    const bankingDetails = organizerConfig.payment_config?.banking_details;
    
    if (!bankingDetails) {
      return {
        success: false,
        error: 'Banking details not configured for manual payments'
      };
    }

    const instructions = `
Please make payment using the following details:

Bank: ${bankingDetails.bank_name}
Account Name: ${bankingDetails.account_holder}
Account Number: ${bankingDetails.account_number}
Branch Code: ${bankingDetails.branch_code}
Amount: R${request.amount.toFixed(2)}
Reference: ${reference}

Please use the reference number exactly as shown above.
Payment confirmation will be processed within 24 hours.
    `.trim();

    return {
      success: true,
      paymentReference: reference,
      instructions
    };
  }

  static async updatePaymentStatus(
    transactionId: string, 
    status: 'completed' | 'failed' | 'cancelled',
    paymentReference?: string
  ): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('payment_transactions')
        .update({
          status,
          payment_reference: paymentReference,
          updated_at: new Date().toISOString()
        })
        .eq('id', transactionId);

      if (error) throw error;

      // If payment completed, update registration status
      if (status === 'completed') {
        const { data: transaction } = await supabase
          .from('payment_transactions')
          .select('registration_id')
          .eq('id', transactionId)
          .single();

        if (transaction) {
          await supabase
            .from('registrations')
            .update({ payment_status: 'paid' })
            .eq('id', transaction.registration_id);
        }
      }

      return true;
    } catch (error) {
      console.error('Error updating payment status:', error);
      return false;
    }
  }
} 